import { useContext, useMemo } from 'react'
import {
    UserContext,
    UserContextType,
    MediaSelection,
    AudioSourceInfo,
    GlobalOutputDeviceType,
    AudioInputSettings,
    AudioInputSelection,
    AudioInputStreamInfo,
    MediaSelectionPrevPreference,
} from '../UserContext'
import {
    isMuseDAWBridgeDevice,
    isMuseRecordingDevice,
    isVbAudioCable,
    selectDefaultInputDevice,
    selectDefaultOutputDevice,
} from '../services/MediaDevices'
import { AppContext, AppContextType, VbAudioCableAutoDeselectionInfo } from '../AppContext'
import { env } from '../services/EnvironmentVariables'
import ElectronIPC from '../services/ElectronIPC'
import clone from 'clone'
import useUserContextRef from '../services/useUserContextRef'
import { logger } from '../services/Logger'
import { MIDIInput, MIDIOutput } from '../types/WebMidi'
import { persistent } from '../services/LocalStorage'
import * as Constants from '../services/Constants'
import { FeatureFlags } from '../services/FeatureFlags'
import useAppContextRef from '../services/useAppContextRef'
import { unstable_batchedUpdates } from 'react-dom'
import useAudioSettingsStoreActions from '../core/useAudioSettingsStoreActions'
import { isSubset, sleep } from '../utils'
import useRoomContextRef from '../services/useRoomContextRef'
import { isDefinedDeviceId } from '../Logic'
import { AnalyticsEvents } from '../services/AnalyticsService'
import useAnalytics from '../services/useAnalytics'
import _ from 'lodash'
import { RoomContext, RoomContextType } from '../RoomContext'

const log = logger.createChildLogger({ name: 'MediaSelectionActions' })

function useMediaSelectionActions() {
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { logUserActivityWithContext } = useAnalytics()
    const { getAudioInputSettings, setAudioInputSettings } = useAudioSettingsStoreActions()

    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()
    const { appStateRef } = useAppContextRef()

    return useMemo(() => {
        const mkAudioInputSelection = (
            source: AudioSourceInfo,
            hint?: { isTalkbackMic?: boolean }
        ): AudioInputSelection | null => {
            const settings: AudioInputSettings | null = getAudioInputSettings(source, hint)
            if (!settings) {
                log.assert('getAudioInputSettings', `getAudioInputSettings returned null.`)
                return null
            }

            // downgrade if audio engine is not available and `settings` contains
            // settings that require the audio engine.
            const hasPermission = (() => {
                if (roomStateRef.current.preRoomUrn || roomStateRef.current.roomUrn) {
                    return roomStateRef.current.roomTierPolicy.allowAudioEngineTx
                } else {
                    return userStateRef.current.profile?.policy?.allowAudioEngineTx ?? false
                }
            })()

            const isAudioEngineAvailable =
                userStateRef.current.audioEngineState.isLoaded && hasPermission && !!source.audioEngineDeviceId

            // Separate out a special case for ASIO because even if the audio engine is not loaded,
            // if the source is ASIO, then we should still use the audio engine (Chrome can't see ASIO).
            if (source.hostApiName === 'ASIO') {
                // if ASIO device is selected but set to use WebRTC (isAudioEngine === false),
                // then force it to use the audio engine.
                if (!settings.isAudioEngine && source.hostApiName === 'ASIO') {
                    settings.isAudioEngine = true
                }
            } else {
                // non-ASIO cases:
                if (settings.isAudioEngine && !isAudioEngineAvailable) {
                    settings.isAudioEngine = false
                    settings.isHighQuality = true
                    if (settings.channelSelection.flat().length === 1) {
                        settings.channelSelection = [[0]]
                    } else {
                        settings.channelSelection = [[0, 1]]
                    }
                }
            }

            return { deviceId: source.deviceId, settings, source }
        }

        const setMediaSelection = async (partial: Partial<MediaSelection>) => {
            dispatch({
                type: 'update_media_selection',
                value: partial,
            })
        }

        const getMuseAudioShareOutputDevice = () => {
            if (env.OS === 'darwin') {
                const devices = userStateRef.current.mediaSources.audioInputs
                const candidate = devices.filter((d) => d.deviceUid === 'MuseAudioShare_UID')[0]
                return candidate || null
            } else if (env.OS === 'win32') {
                // If selected VB Cable Input, then return the nativeDeviceId of VB Cable Output,
                // and the same thing for Virtual Audio Cable ("Line 1").

                // sort by label is a poor man's way of implementing device selection priority, esp. to prioritize
                // "VB Audio Cable" over "VB Audio Cable A, B", etc.
                const audioInputs = [...userStateRef.current.mediaSelection.audioInputs].sort((a, b) =>
                    a.source.label > b.source.label ? 1 : -1
                )

                const candidate = audioInputs.find((a) => a.source.isMuseAudioShare)
                if (candidate) {
                    return candidate.source.matchingLoopbackSource || null
                }
            } else {
                return null
            }
        }

        const checkMuseAudioShareOutputMatch = (
            audioInputsMediaSelection: AudioInputSelection[],
            gldev: GlobalOutputDeviceType | null
        ) => {
            let isOk: boolean | null = null
            if (env.OS === 'darwin') {
                if (audioInputsMediaSelection.find((a) => a.source.isMuseAudioShare)) {
                    isOk = gldev?.uid === 'MuseAudioShare_UID'
                } else {
                    isOk = true
                }
            } else if (env.OS === 'win32') {
                // sort by label is a poor man's way of implementing device selection priority, esp. to prioritize
                // "VB Audio Cable" over "VB Audio Cable A, B", etc.
                audioInputsMediaSelection = [...audioInputsMediaSelection].sort((a, b) =>
                    a.source.label > b.source.label ? 1 : -1
                )

                const selectedSystemAudioInput = audioInputsMediaSelection.find((a) => a.source.isSystemAudio)
                if (selectedSystemAudioInput?.source?.matchingLoopbackSource) {
                    // currently selected global audio output device's label
                    const a = gldev?.label || ''

                    // currently selected system audio input's matching loopback source's label
                    const b = selectedSystemAudioInput.source.matchingLoopbackSource.label || ''

                    isOk = a.indexOf(b) >= 0 || b.indexOf(a) >= 0
                }

                if (isOk == null) {
                    isOk = true
                }
            }

            return isOk
        }

        const vbAudioCableSingleSelectionCheck = (addedDevice: AudioSourceInfo) => {
            const audioInputs = userStateRef.current.mediaStreams.audioInputs

            const vbCableAsMuseAudioShare = audioInputs.find((a) => a.info.isMuseAudioShare && isVbAudioCable(a))
            const vbCableAsMuseDawBridge = audioInputs.find((a) =>
                a.info.deviceId.startsWith('__internal_muse_daw_bridge')
            )

            let result: VbAudioCableAutoDeselectionInfo | null = null

            // check case where we have VB Audio Cable selected as "Share System Audio" and then
            // "Share DAW Audio" is selected.
            if (vbCableAsMuseAudioShare && addedDevice.deviceId.startsWith('__internal_muse_daw_bridge')) {
                result = {
                    deviceToDeselect: vbCableAsMuseAudioShare.info,
                    selectedDevice: addedDevice,
                }
            }

            if (vbCableAsMuseDawBridge && addedDevice.isMuseAudioShare && isVbAudioCable(addedDevice)) {
                result = {
                    deviceToDeselect: vbCableAsMuseDawBridge.info,
                    selectedDevice: addedDevice,
                }
            }

            return result
        }

        const toggleAudioDeviceSelection = (source: AudioSourceInfo, hint?: { isTalkbackMic?: boolean }) => {
            const audioInputs = userStateRef.current.mediaSelection.audioInputs
            if (audioInputs.map((i) => i.deviceId).includes(source.deviceId)) {
                // remove (by filtering on deviceId)
                const newInputs = audioInputs.filter((x) => x.deviceId !== source.deviceId)
                const mediaSelectionPatch: Partial<MediaSelection> = {
                    audioInputs: newInputs,
                }
                if (userStateRef.current.mediaSelection.talkbackMic === source.deviceId) {
                    mediaSelectionPatch.talkbackMic = '_none'
                }
                setMediaSelection(mediaSelectionPatch)

                // remove device from the `mediaSelection.autoRemovedAudioInputs` list.
                const autoRemovedAudioInputsNext = structuredClone(
                    userStateRef.current.mediaSelection.autoRemovedAudioInputs
                )
                delete autoRemovedAudioInputsNext[source.deviceId]
                if (
                    JSON.stringify(autoRemovedAudioInputsNext) !==
                    JSON.stringify(userStateRef.current.mediaSelection.autoRemovedAudioInputs)
                ) {
                    setMediaSelection({ autoRemovedAudioInputs: autoRemovedAudioInputsNext })
                }

                return { added: null, removed: source }
            } else {
                addAudioDeviceSelection(source, hint)
                return { added: source, removed: null }
            }
            logUserActivityWithContext(AnalyticsEvents.AUDIO_INPUT_TOGGLED, source)
        }

        const reloadAudioDeviceSelection = async (device: AudioInputSelection) => {
            if (userStateRef.current.mediaSelection.audioInputs.find((a) => a.deviceId === device.deviceId)) {
                toggleAudioDeviceSelection(device.source) // toggle off
            }

            // poll to see if the device is taken off
            for (let i = 0; i < 10; ++i) {
                await sleep(500)

                const audioInputs = userStateRef.current.mediaStreams.audioInputs
                const found = audioInputs.find((a) => a.info.deviceId === device.deviceId)
                if (!found) {
                    // device is taken off
                    break
                }
            }

            addAudioDeviceSelection(device.source)
        }

        const setTalkbackDevice = (deviceId: string | undefined) => {
            if (isDefinedDeviceId(deviceId)) {
                const selection = userStateRef.current.mediaSelection.audioInputs.find((s) => s.deviceId === deviceId)
                if (selection) {
                    updateSelectedAudioDeviceQualityPreset(selection, 'voice', true)
                } else {
                    setMediaSelection({ talkbackMic: '_none' })
                }
            } else {
                setMediaSelection({ talkbackMic: '_none' })
            }
        }

        const addAudioDeviceSelection = (
            sources: AudioSourceInfo | AudioSourceInfo[],
            hint?: { isTalkbackMic?: boolean }
        ) => {
            const audioInputs = userStateRef.current.mediaSelection.audioInputs

            if (!Array.isArray(sources)) {
                sources = [sources]
            }

            const audioInputsNext = [...audioInputs]
            const myAudioMuteStateNext = { ...roomStateRef.current.myAudioMuteState }

            let talkbackMic = undefined

            for (let source of sources) {
                if (audioInputsNext.map((i) => i.deviceId).includes(source.deviceId)) {
                    continue
                }

                // const added: AudioInputStreamInfo = createAudioInputDescriptor(source)
                // added.enableLocalLoopback = _shouldEnableLocalLoopback(added)

                // check useEnforceVbAudioCableSingleSelection.
                if (env.OS === 'win32') {
                    const info = vbAudioCableSingleSelectionCheck(source)
                    if (info) {
                        appDispatch({
                            type: 'update_app_state',
                            value: {
                                showVbAudioCableAutoDeselectionDialog: info,
                            },
                        })
                        return
                    }
                }

                // get audio settings for this device
                const s: AudioInputSelection | null = mkAudioInputSelection(source, hint)
                if (s) {
                    if (hint?.isTalkbackMic) {
                        talkbackMic = s.deviceId
                    }
                    audioInputsNext.push(s)
                }

                // clear `myAudioMuteState` for the added device
                Object.keys(myAudioMuteStateNext).forEach((extendedDeviceId) => {
                    if ((extendedDeviceId || '').startsWith(source.deviceId)) {
                        delete myAudioMuteStateNext[extendedDeviceId]
                    }
                })
            }

            if (audioInputsNext.length > roomStateRef.current.roomTierPolicy.maxAudioDeviceCount) {
                appDispatch({
                    type: 'update_app_state',
                    value: { showUpgradeToMuseProDialog: { type: 'premiumFeature:noDeviceCountLimit' } },
                })
                return
            }

            const audioInputsPatch: Partial<MediaSelection> = {
                audioInputs: audioInputsNext,
            }
            if (talkbackMic) {
                audioInputsPatch.talkbackMic = talkbackMic
            }
            setMediaSelection(audioInputsPatch)

            if (!_.isEqual(myAudioMuteStateNext, roomStateRef.current.myAudioMuteState)) {
                roomDispatch({ type: 'update_room_context', value: { myAudioMuteState: myAudioMuteStateNext } })
            }
        }

        // Set global output device to "Muse Audio Share"
        const setGlobalOutputDeviceToMuseAudioShare = async (options: { undoWhenScreenShareEnds: boolean }) => {
            let museAudioShareOutput = getMuseAudioShareOutputDevice()
            // museAudioShareNativeDeviceId = '{0.0.0.00000000}.{395121ed-9b77-445f-9410-96a852d4411f}'

            // @ts-ignore
            let nativeDeviceId = museAudioShareOutput?.nativeDeviceId
            log.verbose(`setGlobalOutputDeviceToMuseAudioShare`, `setting global output device:`, {
                museAudioShareOutput_defined: !!museAudioShareOutput,
                label: museAudioShareOutput?.label,
                nativeDeviceId: nativeDeviceId,
            })

            if (!museAudioShareOutput) {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message:
                            'Could not change the audio output device: code 1. ' +
                            'Please change the audio output device manually.',
                    },
                })
                return
            }

            if (!nativeDeviceId) {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message:
                            'Could not change the audio output device: code 2. ' +
                            'Please change the audio output device manually.',
                    },
                })
                return
            }

            dispatch({
                type: 'update_media_selection',
                value: {
                    lastGlobalOutputDeviceBeforeAutoSelection: structuredClone(
                        userStateRef.current.mediaSelection.globalOutputDevice
                    ),
                    undoGlobalOutputDeviceWhenScreenShareEnds: options?.undoWhenScreenShareEnds || false,
                    isGlobalOutputDeviceChangeUpdating: true,
                },
            })

            ElectronIPC.setGlobalAudioOuputDevice({ nativeDeviceId })

            // poll. The return value from the OS is not reliable.
            let success = false
            const CHECK_INTERVAL_MS = 1000
            const CHECK_TIMEOUT_MS = 6000
            for (let t = 0; t < CHECK_TIMEOUT_MS; t += CHECK_INTERVAL_MS) {
                await sleep(CHECK_INTERVAL_MS)

                // Set at the `global_output_device_changed` action handler in UserContext.
                // We use this hack because for some reason, userStateRef.current.mediaSelection.globalOutputDevice
                // has cached value, especially when the change was triggered from the ScreenShareSelectionPage.
                // @ts-ignore
                const currGlobalOutputDeviceNativeDeviceId = window.globalOutputDeviceNativeDeviceId
                if (currGlobalOutputDeviceNativeDeviceId == nativeDeviceId) {
                    success = true
                    break
                }
            }

            dispatch({
                type: 'update_media_selection',
                value: { isGlobalOutputDeviceChangeUpdating: false },
            })

            if (!success) {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message:
                            'Could not change the audio output device: code 3. ' +
                            'Please change the audio output device manually.',
                    },
                })
                return
            }

            const label = env.OS === 'darwin' ? 'Muse Audio Share' : `"${museAudioShareOutput.label}"`
            appDispatch({
                type: 'root_snackbar_history_append',
                value: {
                    severity: 'success',
                    message: `Audio output device set to ${label}.`,
                },
            })
        }

        const hasNonWebRtcChannels = (settings: AudioInputSettings) => {
            let b = false
            settings.channelSelection.flat().forEach((a) => {
                if (a >= 2) {
                    b = true
                }
            })
            return b
        }

        /**
         * Update the device properties (AudioInputDescriptor) of an audio device that is currently
         * selected.
         */
        const updateSelectedAudioDeviceProperties = (
            deviceId: string,
            properties: Partial<AudioInputStreamInfo>,
            isTalkback: boolean = false
        ) => {
            log.info(
                'updateSelectedAudioDeviceProperties',
                `deviceId="${deviceId}" properties=${JSON.stringify(properties)}`
            )
            const mediaSelectionNext = clone(userStateRef.current.mediaSelection)
            const itemToUpdate = mediaSelectionNext.audioInputs.find((i) => i.deviceId === deviceId)
            if (!itemToUpdate) {
                log.error(
                    'updateSelectedAudioDeviceProperties',
                    `failed to find deviceId="${deviceId}" ` + `in mediaSelection.audioInputs. No-op.`
                )
                return
            }

            // copy fields over from `properties` to `itemToUpdate`.
            Object.assign(itemToUpdate.settings, properties)

            // policy: use audio engine if non-webrtc channels selected.
            if (hasNonWebRtcChannels(itemToUpdate.settings) && !itemToUpdate.settings.isAudioEngine) {
                const hasPermission = roomStateRef.current.roomTierPolicy.allowAudioEngineTx
                const isAudioEngineAvailable = userStateRef.current.audioEngineState.isLoaded && hasPermission

                if (isAudioEngineAvailable) {
                    itemToUpdate.settings.isAudioEngine = true
                    itemToUpdate.settings.isCompressed = true
                    itemToUpdate.settings.compressionBitrate = 192_000

                    // TODO: show info dialog
                } else {
                    // filter out channel selection greater than 2.
                    itemToUpdate.settings.channelSelection = itemToUpdate.settings.channelSelection
                        .map((chgrp) => {
                            return chgrp.filter((ch) => ch < 2)
                        })
                        .filter((chgrp) => chgrp.length > 0)
                }
            }

            // channel policy: sort the channels
            itemToUpdate.settings.channelSelection.sort((a, b) => {
                return a[0] < b[0] ? -1 : 1
            })

            // channel policy e.g., if chgrp==[1,2] and [1] already exists, then remove [1].
            const lenGte2ChgrpChs = itemToUpdate.settings.channelSelection.filter((chgrp) => chgrp.length >= 2).flat()
            itemToUpdate.settings.channelSelection = itemToUpdate.settings.channelSelection.filter((chgrp) => {
                // check if is a subset of any >2-element chgrp and remove if so.
                if (chgrp.length === 1 && lenGte2ChgrpChs.includes(chgrp[0])) {
                    return false // remove
                }
                return true // keep
            })

            // Update talkback mic
            const isCurrentTalkback = userStateRef.current.mediaSelection.talkbackMic === deviceId
            const isVoice = !properties.isHighQuality && !properties.isAudioEngine
            let nextTalkback = userStateRef.current.mediaSelection.talkbackMic
            if (isCurrentTalkback && !isVoice) {
                // Unset as talkback if it no longer qualifies.
                itemToUpdate.settings.isVoiceQuality = false
                nextTalkback = '_none'
            } else if (isTalkback && isVoice) {
                nextTalkback = deviceId
            }

            setAudioInputSettings(deviceId, itemToUpdate.settings)

            setMediaSelection({
                audioInputs: mediaSelectionNext.audioInputs,
                talkbackMic: nextTalkback,
            })

            // if (properties.isHighQuality) {
            //     appDispatch({
            //         type: 'new_message_dialog',
            //         value: {
            //             show: true,
            //             id: 'hq_audio_notification',
            //             text: t('Enabling HQ audio disables echo cancellation.'),
            //             positiveButton: t('OK'),
            //             negativeButton: t("Don't show again"),
            //             negativeButtonDoNotShowAgain: true,
            //         }
            //     })
            // }
        }

        const updateSelectedAudioDeviceQualityPreset = (
            selection: AudioInputSelection,
            qualityPresetCode: string,
            isTalkback: boolean = false
        ) => {
            const hasPermission = roomStateRef.current.roomTierPolicy.allowAudioEngineTx

            const presetData = Constants.getAudioQualityPresets().find((a) => a.code === qualityPresetCode)
            if (!presetData) {
                return
            }

            if (!hasPermission && !presetData.isWebRtc) {
                // no permission
                return
            }

            const s = structuredClone(selection.settings)
            switch (qualityPresetCode) {
                case 'voice': {
                    s.isAudioEngine = false
                    s.isHighQuality = false
                    s.isVoiceQuality = true
                    break
                }
                case 'enhanced': {
                    s.isAudioEngine = false
                    s.isHighQuality = true
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_essential': {
                    s.isAudioEngine = true
                    s.isCompressed = true
                    s.compressionBitrate = 96_000
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_standard': {
                    s.isAudioEngine = true
                    s.isCompressed = true
                    s.compressionBitrate = 128_000
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_enhanced': {
                    s.isAudioEngine = true
                    s.isCompressed = true
                    s.compressionBitrate = 192_000
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_hd': {
                    s.isAudioEngine = true
                    s.isCompressed = true
                    s.compressionBitrate = 256_000
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_cd': {
                    s.isAudioEngine = true
                    s.isCompressed = false
                    s.bitDepth = 16
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_studio': {
                    s.isAudioEngine = true
                    s.isCompressed = false
                    s.bitDepth = 24
                    s.isVoiceQuality = false
                    break
                }
                case 'pro_highest': {
                    s.isAudioEngine = true
                    s.isCompressed = false
                    s.bitDepth = 32
                    s.isVoiceQuality = false
                    break
                }
            }

            updateSelectedAudioDeviceProperties(selection.deviceId, s, isTalkback)
        }

        const updateSelectedAudioDeviceAddChannelGroup = (selection: AudioInputSelection, chgrp: number[]) => {
            let channelSelectionNext = structuredClone(selection.settings.channelSelection)

            // e.g., if chgrp==[1],[2] and [1,2] is added, then replace [1],[2] with [1,2].
            if (chgrp.length > 1) {
                const isChannelGroupSingularChannel = (chgrp: number[], ch: number) => {
                    return chgrp.length === 1 && chgrp[0] === ch
                }

                const bs = chgrp.map((ch) => {
                    return !!channelSelectionNext.find((g) => isChannelGroupSingularChannel(g, ch))
                })
                if (bs.every((b) => b === true)) {
                    chgrp.forEach((ch) => {
                        // filter out
                        channelSelectionNext = channelSelectionNext.filter((g) => !isChannelGroupSingularChannel(g, ch))
                    })
                }
            }

            channelSelectionNext.push(chgrp)

            // e.g., if chgrp==[1,2] and [1] already exists, then remove [1].
            const lenGte2ChgrpChs = channelSelectionNext.filter((chgrp) => chgrp.length >= 2).flat()
            channelSelectionNext = channelSelectionNext.filter((chgrp) => {
                // check if is a subset of any >2-element chgrp and remove if so.
                if (chgrp.length === 1 && lenGte2ChgrpChs.includes(chgrp[0])) {
                    return false // remove
                }
                return true // keep
            })

            updateSelectedAudioDeviceProperties(selection.source.deviceId, {
                channelSelection: channelSelectionNext,
            })
            logUserActivityWithContext(AnalyticsEvents.CHANNEL_GROUP_ADDED, {
                ...selection.source,
                channelSelection: channelSelectionNext,
            })
        }

        const updateSelectedAudioDeviceReplaceChannelGroup = (
            selection: AudioInputSelection,
            prevChgrp: number[],
            newChgrp: number[]
        ) => {
            let channelSelectionNext = structuredClone(selection.settings.channelSelection)
            channelSelectionNext = channelSelectionNext.filter((a) => {
                return JSON.stringify(a) !== JSON.stringify(prevChgrp)
            })

            // e.g., if chgrp==[1,2] and [1] already exists, then remove [1].
            const hasSubset = channelSelectionNext.find((x) => isSubset(newChgrp, x))

            if (!hasSubset) {
                channelSelectionNext.push(newChgrp)
            }

            updateSelectedAudioDeviceProperties(selection.source.deviceId, {
                channelSelection: channelSelectionNext,
            })
            logUserActivityWithContext(AnalyticsEvents.CHANNEL_GROUP_REPLACED, {
                ...selection.source,
                channelSelection: channelSelectionNext,
            })
        }

        const updateSelectedAudioDeviceRemoveChannelGroup = (selection: AudioInputSelection, chgrp: number[]) => {
            let channelSelectionNext = structuredClone(selection.settings.channelSelection)
            channelSelectionNext = channelSelectionNext.filter((a) => {
                return JSON.stringify(a) !== JSON.stringify(chgrp)
            })
            updateSelectedAudioDeviceProperties(selection.source.deviceId, {
                channelSelection: channelSelectionNext,
            })
            logUserActivityWithContext(AnalyticsEvents.CHANNEL_GROUP_REMOVED, {
                ...selection.source,
                channelSelection: channelSelectionNext,
            })
        }

        /**
         * select Muse DAW Bridge device if applicable.
         */
        const enableMuseDawBridge = (options?: { force?: boolean }) => {
            console.log(
                'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: musePluginState.isConnected===' +
                    appStateRef.current.appState.musePluginState.isConnected
            )
            if (!appStateRef.current.appState.musePluginState.isConnected) {
                if (!options?.force) {
                    console.log(
                        'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: MusePlugin is not connected ' +
                            'and options.force is not true so no-op.'
                    )
                    return
                } else {
                    console.log(
                        'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: MusePlugin is not connected ' +
                            'but options.force is true so we continue on to enable the Muse DAW Bridge.'
                    )
                }
            }

            const museDAWAudioBridge =
                userStateRef.current.mediaSources.audioInputs.filter((a) => isMuseDAWBridgeDevice(a))[0] || null
            console.log(
                'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: got museDAWAudioBridge=%O',
                museDAWAudioBridge
            )
            if (!museDAWAudioBridge) {
                console.log(
                    'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: Muse DAW Bridge device not found in mediaSources ' +
                        'so no-op.'
                )
                return
            }

            const match =
                userStateRef.current.mediaSelection.audioInputs.filter(
                    (a) => a.deviceId === museDAWAudioBridge.deviceId
                )[0] || null
            if (!match) {
                console.log(
                    'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: mediaSelection does not include this device ' +
                        'so adding it. (deviceId=%O)',
                    museDAWAudioBridge.deviceId
                )
                // const added: AudioInputDescriptor = createAudioInputDescriptor(museDAWAudioBridge)
                toggleAudioDeviceSelection(museDAWAudioBridge)
            } else {
                console.log(
                    'useMuseDAWAudioSelectionManager: enableMuseDAWBridge: device (deviceId=%O) already is selected ' +
                        'so no-op.',
                    match.deviceId
                )
            }
        }

        const disableMuseDawBridge = () => {
            const match = userStateRef.current.mediaStreams.audioInputs.filter((a) => a.info.isMuseDAWBridge)[0] || null
            if (match) {
                console.log(
                    'useMuseDAWAudioSelectionManager: disableMuseDAWBridge: mediaStreams does include MuseDAWBridge ' +
                        'so removing it. (deviceId=%O)',
                    match.info.deviceId
                )

                // const audioInputsNext = userStateRef.current.mediaStreams.audioInputs.filter(a => !a.info.isMuseDAWBridge)

                const museDAWAudioBridge =
                    userStateRef.current.mediaSources.audioInputs.filter((a) => isMuseDAWBridgeDevice(a))[0] || null
                if (museDAWAudioBridge) {
                    toggleAudioDeviceSelection(museDAWAudioBridge)
                }
            } else {
                console.log(
                    'useMuseDAWAudioSelectionManager: disableMuseDAWBridge: no MuseDAWBridge in mediaSelection ' +
                        'so no-op.'
                )
            }
        }

        const chooseDefaultMediaSelection = async (mediaSources: {
            video: MediaDeviceInfo[]
            audioInputs: AudioSourceInfo[]
            audioOutputs: AudioSourceInfo[]
            midiInputs: MIDIInput[]
            midiOutputs: MIDIOutput[]
        }) => {
            const prevPref = persistent.getObject(
                Constants.media_selection_prev_preference
            ) as MediaSelectionPrevPreference | null

            log.verbose(`chooseDefaultMediaSelection`, `queried prevPref and got:`, { prevPref })

            const delta: Partial<MediaSelection> = {}
            if (!appStateRef.current.appSettings.noSelectDefaultCamera) {
                // if there is a previous media selection preference:
                if (prevPref?.video) {
                    // prevPrev.video is the label of the device.
                    const match = mediaSources.video.filter((a) => a.label === prevPref.video)[0]
                    if (match) {
                        delta.video = match.deviceId
                    }
                }
                // If there was no match, then select first.
                if (!delta.video) {
                    if (mediaSources.video[0]) {
                        delta.video = mediaSources.video[0].deviceId
                    }
                }
            }

            if (mediaSources.audioInputs.length > 0) {
                delta.audioInputs = []

                // Handle talkbackMic:
                // Do not set the talkbackMic if the user explicitly wanted none.
                if (prevPref?.talkbackMic === '_none') {
                    log.verbose(
                        `chooseDefaultMediaSelection`,
                        `prevPref.talkbackMic is "_none" so setting delta.talkbackMic to "_none".`
                    )
                    delta.talkbackMic = '_none'
                } else {
                    const match = mediaSources.audioInputs.find((a) => a.label === prevPref?.talkbackMic)
                    if (match) {
                        const s = mkAudioInputSelection(match, { isTalkbackMic: true })
                        if (s) {
                            delta.talkbackMic = s.deviceId
                            delta.audioInputs?.push(s)
                            log.verbose(
                                `chooseDefaultMediaSelection`,
                                `found prevPref.talkbackMic "${s.source.label}".`
                            )
                        }
                    } else {
                        // guess a reasonable default input device by label.
                        let chosen = selectDefaultInputDevice(mediaSources.audioInputs)
                        if (chosen) {
                            const s = mkAudioInputSelection(chosen, { isTalkbackMic: true })
                            if (s) {
                                delta.talkbackMic = s.deviceId
                                delta.audioInputs?.push(s)
                            }
                        }
                    }
                }

                // Attempt selection based on prevPref.
                if (prevPref?.audioInputs) {
                    prevPref.audioInputs.forEach((label) => {
                        const match = mediaSources.audioInputs.find((a) => a.label === label)
                        if (match) {
                            const s = mkAudioInputSelection(match)
                            if (s) {
                                // Recall: `delta.audioInputs` may contain talkbackMic from the previous block; do not
                                // double-add.
                                if (!delta.audioInputs?.find((a) => a.deviceId === s.deviceId)) {
                                    log.verbose(
                                        `chooseDefaultMediaSelection`,
                                        `adding device "${s.source.label}" from prevPref.audioInputs.`
                                    )
                                    delta.audioInputs?.push(s)
                                }
                            }
                        }
                    })
                }
            } // end handling `audioInputs`

            if (mediaSources.audioOutputs.length > 0) {
                // If there is a previous media selection preference:
                if (prevPref?.audioOutput) {
                    const match = mediaSources.audioOutputs.find((a) => a.label === prevPref.audioOutput)
                    if (match) {
                        delta.audioOutput = match.deviceId
                    }
                }
                // If still not selected, then:
                if (!delta.audioOutput) {
                    delta.audioOutput = selectDefaultOutputDevice(mediaSources.audioOutputs)?.deviceId || '_none'
                }
            }

            // for simple-remote-recording, find and set the Muse Recording audio output device.
            if (FeatureFlags.isEnabled('simple-remote-recording')) {
                let museRecordingDevice =
                    userStateRef.current.mediaSources.audioOutputsAll.filter((d) => {
                        const pass = isMuseRecordingDevice(d)
                        return pass
                    })[0] || null

                if (env.OS === 'win32') {
                    // On Windows, the user can select the recording output device so
                    // see prevPref first.
                    if (prevPref?.audioOutputMuseRecording) {
                        museRecordingDevice =
                            userStateRef.current.mediaSources.audioOutputsAll.filter(
                                (d) => d.label === prevPref.audioOutputMuseRecording
                            )[0] || null
                    }
                }

                if (museRecordingDevice) {
                    log.verbose(
                        `chooseDefaultMediaSelection`,
                        `simple-remote-recording: passing all ` +
                            `peer audio out to museRecordingDevice (deviceId="${museRecordingDevice.deviceId}", ` +
                            `label="${museRecordingDevice.label}").`
                    )
                    dispatch({
                        type: 'update_media_selection',
                        value: {
                            audioOutputMuseRecording: museRecordingDevice.deviceId,
                        },
                    })
                } else {
                    log.verbose(
                        `chooseDefaultMediaSelection`,
                        `simple-remote-recording: Muse recording ` + `device not found; museRecordingDevice=null.`
                    )
                }
            }

            // select Muse DAW Bridge if Muse Plugin is connected.
            if (appStateRef.current.appState.musePluginState.isConnected) {
                const museDAWAudioBridge =
                    userStateRef.current.mediaSources.audioInputs.find((a) => isMuseDAWBridgeDevice(a)) || null
                console.log('chooseDefaultMediaSelection: found museDAWAudioBridge=%O', museDAWAudioBridge)
                if (!museDAWAudioBridge) {
                    console.log(
                        'chooseDefaultMediaSelection: Muse DAW Bridge device not found in mediaSources ' + 'so no-op.'
                    )
                } else {
                    if (!Array.isArray(delta.audioInputs)) {
                        delta.audioInputs = []
                    }
                    const s = mkAudioInputSelection(museDAWAudioBridge)
                    if (s) {
                        delta.audioInputs.push(s)
                    }
                }
            }

            // This flag indicates that the defaults have been loaded.
            delta.isDefaultMediaSelectionLoaded = true

            log.verbose('chooseDefaultMediaSelection', `choosing initial default media selection: delta=`, { delta })
            setMediaSelection(delta)
        }

        async function openBrowserDesktopStreamSelector() {
            let captureStream: MediaStream | null = null
            try {
                captureStream = await navigator.mediaDevices.getDisplayMedia()
            } catch (e) {
                if (e instanceof Error && e.name !== 'NotAllowedError') {
                    log.error('openBrowserDesktopStreamSelector: error getting display media: ', e.message)
                }
            }
            if (captureStream instanceof MediaStream) {
                // If previous desktop stream exists, stop it.
                if (userStateRef.current.mediaStreams.desktop instanceof MediaStream) {
                    try {
                        userStateRef.current.mediaStreams.desktop.getTracks().forEach((track) => {
                            track.stop()
                        })
                    } catch (e) {}
                }
                unstable_batchedUpdates(() => {
                    // @ts-ignore
                    dispatch({
                        type: 'update_media_sources',
                        value: {
                            desktop: [{ display_id: '__browser_desktop', id: '__browser_desktop', name: 'Screen' }],
                        },
                    })
                    dispatch({
                        type: 'update_media_selection',
                        value: { desktop: '__browser_desktop' },
                    })
                    dispatch({
                        type: 'update_media_streams',
                        value: { desktop: captureStream },
                    })
                })

                // register handler to unset desktop if stream stopped.
                const track = captureStream.getVideoTracks()[0]
                if (track) {
                    track.addEventListener('ended', () => {
                        if (captureStream) {
                            captureStream.getTracks().forEach((track) => track.stop())
                        }
                        unstable_batchedUpdates(() => {
                            // @ts-ignore
                            dispatch({
                                type: 'update_media_sources',
                                value: { desktop: [] },
                            })
                            dispatch({
                                type: 'update_media_selection',
                                value: { desktop: '_none' },
                            })
                            dispatch({
                                type: 'update_media_streams',
                                value: { desktop: null },
                            })
                        })
                    })
                }
            } else if (!(userStateRef.current.mediaStreams.desktop instanceof MediaStream)) {
                // disable stream if there is no current stream
                unstable_batchedUpdates(() => {
                    // @ts-ignore
                    dispatch({
                        type: 'update_media_sources',
                        value: { desktop: [] },
                    })
                    dispatch({
                        type: 'update_media_selection',
                        value: { desktop: '_none' },
                    })
                    dispatch({
                        type: 'update_media_streams',
                        value: { desktop: null },
                    })
                })
            }
        }

        function openShareScreenDialog() {
            if (env.IS_ELECTRON) {
                appDispatch({
                    type: 'update_app_state',
                    value: { showScreenShareSelectionPage: { isOpen: true } },
                })
            } else {
                openBrowserDesktopStreamSelector()
            }
        }

        // NOTE: unused since v1.5.0.
        // const setMuseAudioShareLocalLoopback = (on: boolean) => {
        //     const audioInputs_next = clone(userStateRef.current.mediaSelection.audioInputs)
        //     let changed = false
        //     if (on) {
        //         // loopback on
        //         audioInputs_next.forEach(a => {
        //             if (a.isMuseAudioShare && !a.enableLocalLoopback) {
        //                 a.enableLocalLoopback = true
        //                 changed = true
        //             }
        //         })
        //     } else {
        //         // loopback off
        //         audioInputs_next.forEach(a => {
        //             if ((a.isMuseAudioShare || a.isMuseDAWBridge) && a.enableLocalLoopback) {
        //                 a.enableLocalLoopback = false
        //                 changed = true
        //             }
        //         })
        //     }

        //     if (changed) {
        //         changeMediaSelection({ audioInputs: audioInputs_next })
        //     }
        // }

        /// const detectOutputFeedbackLoopCondition = () => {
        ///     const { audioOutput, audioInputs } = userStateRef.current.mediaSelection
        ///     // An invalid audioOutput: If there is an `isInternal` audioInput in the audioInputs[]
        ///     // and the currently selected audioOutput is also `isInternal`, then we should show an
        ///     // error message.
        ///     if (audioOutput && audioOutput !== '_none') {
        ///         if (audioInputs.filter(i => i.isSystemAudio).length > 0) {
        ///             const audioOutputInfo: MediaDeviceInfo | null
        ///                 = userStateRef.current.mediaSources.audioOutputs.filter(o => o.deviceId === audioOutput)[0]
        ///             if (audioOutputInfo == null) {
        ///                 return false
        ///             }
        ///             if (isSystemAudio(audioOutputInfo)) {
        ///                 return true
        ///             }
        ///         }
        ///     }
        ///     return false
        /// }

        /// const hasFeedbackLoop = detectOutputFeedbackLoopCondition()

        /**
         * Gets the current selection info of an audio source. Returns selection info if
         * the device is currently selected, null otherwise.
         */
        const getAudioInputSelection = (source: AudioSourceInfo): AudioInputSelection | null => {
            const selection = userStateRef.current.mediaSelection.audioInputs.find(
                (i) => i.deviceId === source.deviceId
            )
            return selection ?? null
        }

        const getAudioInputSelectionStream = (selection: AudioInputSelection) => {
            const stream = userStateRef.current.mediaStreams.audioInputs.find(
                (i) => i.info.deviceId === selection.deviceId
            )
            return stream ?? null
        }

        return {
            mkAudioInputSelection,
            setMediaSelection,
            toggleAudioDeviceSelection,
            reloadAudioDeviceSelection,
            setTalkbackDevice,
            addAudioDeviceSelection,
            updateSelectedAudioDeviceProperties,
            updateSelectedAudioDeviceQualityPreset,
            updateSelectedAudioDeviceAddChannelGroup,
            updateSelectedAudioDeviceReplaceChannelGroup,
            updateSelectedAudioDeviceRemoveChannelGroup,
            setGlobalOutputDeviceToMuseAudioShare,
            checkMuseAudioShareOutputMatch,
            enableMuseDawBridge,
            disableMuseDawBridge,
            chooseDefaultMediaSelection,
            openBrowserDesktopStreamSelector,
            openShareScreenDialog,
            getAudioInputSelection,
            getAudioInputSelectionStream,
        }
    }, [])
}

export default useMediaSelectionActions
